import React, { useCallback, useEffect, useRef, useState } from "react";
import ApplicationQueue from "../../common/lib/ApplicationQueue";
import useAPIError from "../../common/lib/APIError";
import NmefSpinner from "../../common/lib/NmefSpinner";
import { ApplicationRepo } from "../../repositories";
import { QueueActivityDataAPI } from "../../common/lib/API/QueueActivityDataAPI";
import ApplicationContext from "../../common/lib/ApplicationQueue/ApplicationContext";
import AlertModal from "../../common/lib/Loaders/AlertModal";

// 30s background polling interval per FAS-7443 / FAS-7699.
const QUEUE_POLL_INTERVAL_MS = 30000;

const QUEUE_DIFF_FIELDS = [
	"id",
	"status",
	"application_state",
	"is_locked",
	"locked_by_email",
	"on_hold",
	"sort_order_hours_in_queue",
	"date_entered_in_current_queue",
	"credit_app_expiration_date",
	"runtime",
	"invoice_amount",
	"resubmit_count",
	"applicant_name",
	"broker_short_name",
	"account_manager_id",
	"record_type",
	"deal_type",
	"deal_setup",
	"portalSource",
	"broker_id",
	"parent_broker",
];

const rowsEqualForDisplay = (a, b) => {
	for (const f of QUEUE_DIFF_FIELDS) {
		if (a?.[f] !== b?.[f]) return false;
	}
	return true;
};

const mergeApplications = (prev, next) => {
	if (!Array.isArray(prev) || prev.length === 0) return next;
	const prevById = new Map(prev.map((row) => [row.id, row]));
	return next.map((row) => {
		const prior = prevById.get(row.id);
		if (prior && rowsEqualForDisplay(prior, row)) return prior;
		return row;
	});
};

const filterProductionApps = (apps) =>
	apps.filter((application) => {
		if (
			application.id?.includes("PRE-PROD_") ||
			application.id?.includes("BETA-")
		)
			return false;
		return true;
	});

const QueueManager = (props) => {
	const { addError } = useAPIError();
	const [applications, setApplications] = useState(null);
	const [isLoading, setLoading] = useState(false);
	const [isChecking, setChecking] = useState(false);
	const [isSearch, setIsSearch] = useState(false);
	const [searchInput, setSearchInput] = useState("");
	const [totalApp, setTotalApp] = useState(0);
	const [totalAppsInQueue, setTotalAppsInQueue] = useState(0);
	const [searchCriteria, setSearchCriteria] = useState("id");
	const [isOpen, setIsOpen] = useState({ open: false, app: "" });
	const [isRefresh, setIsRefresh] = useState(false);
	const { isQueueVisible = true } = props;

	const propsRef = useRef(props);
	useEffect(() => {
		propsRef.current = props;
	});

	const [documentHidden, setDocumentHidden] = useState(
		typeof document !== "undefined" ? !!document.hidden : false
	);
	useEffect(() => {
		const onVisibility = () => setDocumentHidden(!!document.hidden);
		document.addEventListener("visibilitychange", onVisibility);
		return () => document.removeEventListener("visibilitychange", onVisibility);
	}, []);

	const loadQueue = useCallback(
		async ({ silent = false, surfaceErrors = true } = {}) => {
			if (!silent) setLoading(true);
			try {
				const { status } = propsRef.current;
				let apps = await ApplicationRepo.getApplications(status);
				apps = filterProductionApps(apps);
				if (typeof apps == "object") {
					setApplications((prev) =>
						silent ? mergeApplications(prev, apps) : apps
					);
					setTotalAppsInQueue(apps?.length);
					setTotalApp(apps?.length);
				}
			} catch (error) {
				if (surfaceErrors) addError(error.message);
				if (!silent) setTotalApp(0);
			} finally {
				if (!silent) setLoading(false);
			}
		},
		[addError]
	);

	useEffect(() => {
		loadQueue({ silent: false });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!isRefresh) return;
		(async () => {
			try {
				await loadQueue({ silent: true });
			} finally {
				setIsRefresh(false);
			}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isRefresh]);

	const pollingActive = isQueueVisible && !documentHidden;
	const skipImmediateRef = useRef(true);
	useEffect(() => {
		if (!pollingActive) return undefined;
		if (!skipImmediateRef.current) {
			loadQueue({ silent: true, surfaceErrors: false });
		}
		skipImmediateRef.current = false;
		const id = setInterval(() => {
			loadQueue({ silent: true, surfaceErrors: false });
		}, QUEUE_POLL_INTERVAL_MS);
		return () => clearInterval(id);
	}, [pollingActive, loadQueue]);

	const onAppSelected = async (actionType, selectedApp) => {
		try {
			setChecking(true);
			if (actionType.action === "Unlock") {
				await ApplicationRepo.submitApplication(selectedApp, "unlock");
				setApplications((prev) =>
					prev?.map((item) =>
						item.id === selectedApp.id ? { ...selectedApp, is_locked: false } : item
					)
				);
			} else if (actionType.action === "Start Work") {
				const response = await ApplicationRepo.submitApplication(
					selectedApp,
					"lock"
				);
				setApplications((prev) =>
					prev?.map((item) => (item.id === selectedApp.id ? response : item))
				);
			} else if (actionType.action === "Done") {
				const logs = await QueueActivityDataAPI.getSystemActivityLogs(selectedApp.id);
				const cameFromResubmit = logs?.some((log) => log.application_status === "Resubmit");
				if (cameFromResubmit)
					await ApplicationRepo.updateApplicaiton({ ...selectedApp, status: "Resubmit", is_locked: false, locked_by_name: null, locked_by_email: null });
				else if (selectedApp.application_state !== "Canceled")
					await ApplicationRepo.submitApplication(selectedApp, "submit");
				setApplications((prev) => prev?.filter((item) => item.id !== selectedApp.id));
			}
			!props.onAppSelected || props.onAppSelected(actionType, selectedApp);
		} catch (error) {
			addError(error.message);
		} finally {
			setChecking(false);
		}
	};

	const handleAppSearchByAPI = async () => {
		try {
			let apps = [];
			if (!props) return;
			setIsSearch(true);
			if (searchInput && searchCriteria) {
				let filterData = {
					status: props.status,
					application_state: "open",
					is_special: false,
					[searchCriteria]: searchInput,
				};
				apps = await ApplicationRepo.getSearchApplications(filterData);
			} else {
				apps = await ApplicationRepo.getApplications(props.status);
				setSearchCriteria("id");
				setTotalAppsInQueue(apps?.length);
			}
			if (typeof apps == "object") {
				apps = filterProductionApps(apps);
				setApplications(apps);
				setTotalApp(apps?.length);
			}
		} catch (error) {
			addError(error.message);
			setTotalApp(0);
		} finally {
			setIsSearch(false);
		}
	};
	const handleChangeCriteria = (data) => {
		setSearchCriteria(data);
		setSearchInput("");
	};
	const handleSearch = (event) => {
		if (event) setSearchInput(event.target.value);
	};
	const handleCollapse = (app) => {
		if (isOpen?.app == app) {
			setIsOpen({ open: false, app: "" });
		} else {
			setIsOpen({ open: true, app: app });
		}
	};

	window.updateApplicationsList = (app) => {
		setApplications((prev) =>
			prev?.map((item) => {
				if (item.id === app.id) {
					return { ...item, application_state: app.application_state };
				}
				return item;
			})
		);
	};

	window.refreshQueue = () => setIsRefresh(true);

	return (
		<>
			<div id="qmanager-container">
				<ApplicationContext.Provider
					value={{
						handleCollapse,
						handleSearch,
						isOpen,
						totalApp,
						totalAppsInQueue,
						isSearch,
						onAppSelected,
						handleChangeCriteria,
						searchCriteria,
						setSearchInput,
						searchInput,
						setSearchCriteria,
						handleAppSearchByAPI,
						queueDrawerOpen: props.queueDrawerOpen,
						toggleQueueDrawer: props.toggleQueueDrawer,
					}}>
					{isLoading ? (
						<div style={{ padding: 20 }}>
							<NmefSpinner />
						</div>
					) : applications ? (
						<ApplicationQueue applicationList={applications} setIsRefresh={setIsRefresh} />
					) : (
						<div> No App Found</div>
					)}
				</ApplicationContext.Provider>
			</div>
			<AlertModal
				openModal={isChecking}
				title={"Application Status Check"}
				message={"Please wait, Checking the status of application..."}
			/>
		</>
	);
};
export default QueueManager;
